import BaseService from './BaseService';

// An advertiser page an Ad Library fetch can be filtered to.
// PageId is Meta's Ad Library page id — NOT necessarily the number in the
// page's profile URL, which is why pageUrl is stored alongside it.
export interface AdvertiserPage {
  pageId: string;
  pageName: string;
  pageUrl: string | null;
  notes: string | null;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  // The page's last daily fetch as part of a followed brand (static ads).
  // Null until it has been fetched once.
  staticFollowCheckedAt: string | null;
  staticFollowNewAds: number | null;
  staticFollowError: string | null;
  staticFollowErrorAt: string | null;
}

/**
 * A named set of pages, so a brand advertising from several pages is one thing
 * to target. Membership is resolved when a scrape runs, so adding a page to a
 * group widens every keyword already targeting it.
 */
export interface AdvertiserPageGroup {
  groupId: string;
  groupName: string;
  notes: string | null;
  isActive: boolean;
  /** Followed for static ads: its pages are fetched every morning. Null = not followed. */
  followStatics: boolean | null;
  pageIds: string[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What /v1/facebook-page-id returns for a pasted page link. */
export interface FacebookPageLookup {
  id: string;
  slug: string | null;
  name: string | null;
  category: string | null;
  profilePhotoUrl: string | null;
  url: string | null;
}

// One shared list for Video Creative and Static Creative — an advertiser worth
// watching is worth watching in either format.
class AdvertiserPagesService extends BaseService {
  private basePath = 'v1/advertiser-pages';

  async list(activeOnly = true): Promise<{ pages: AdvertiserPage[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(this.basePath, { params: { activeOnly } }),
      'Error loading advertiser pages',
    );
  }

  // Errors propagate: the backend rejects a non-numeric page id with a message
  // that says exactly what went wrong, and that is the useful thing to show.
  async create(body: {
    pageId: string;
    pageName: string;
    pageUrl?: string;
    notes?: string;
    createdBy?: string;
  }): Promise<{ page: AdvertiserPage }> {
    await this._getAccessToken();
    const resp = await this.http.post(this.basePath, body);
    return resp.data;
  }

  async update(pageId: string, body: Partial<AdvertiserPage>): Promise<{ page: AdvertiserPage }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/${pageId}`, body);
    return resp.data;
  }

  /** Deactivates rather than deletes — a keyword may still reference it. */
  async deactivate(pageId: string): Promise<{ deactivated: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/${pageId}`);
    return resp.data;
  }

  // ── Groups ──

  async listGroups(activeOnly = true): Promise<{ groups: AdvertiserPageGroup[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/groups`, { params: { activeOnly } }),
      'Error loading advertiser page groups',
    );
  }

  async createGroup(body: { groupName: string; pageIds?: string[]; notes?: string; createdBy?: string }): Promise<{ group: AdvertiserPageGroup }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/groups`, body);
    return resp.data;
  }

  /** Membership is replaced wholesale — a group is edited as a set. */
  async updateGroup(groupId: string, body: { groupName?: string; pageIds?: string[]; notes?: string; isActive?: boolean; followStatics?: boolean }): Promise<{ group: AdvertiserPageGroup }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/groups/${groupId}`, body);
    return resp.data;
  }

  async deactivateGroup(groupId: string): Promise<{ deactivated: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/groups/${groupId}`);
    return resp.data;
  }

  /**
   * Resolve a pasted Facebook page link to its Ad Library page id and details.
   *
   * A different route (/v1/facebook-page-id), but it exists to serve this UI, so
   * it lives on this service rather than making callers know about two.
   *
   * Worth knowing: the id in a profile.php URL is sometimes an alias, not the
   * page id, so this genuinely has to be looked up rather than parsed out of
   * the link. Errors propagate — "no such page" and "Facebook refused us" are
   * different problems and the caller should say which happened.
   */
  async lookupByUrl(url: string): Promise<FacebookPageLookup> {
    await this._getAccessToken();
    const resp = await this.http.get('v1/facebook-page-id', { params: { url } });
    return resp.data;
  }
}

export default AdvertiserPagesService;
